import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { FiX, FiHelpCircle, FiChevronDown } from "react-icons/fi";
import logo from "../assets/teliolabs-logo.png";
import { sidebarFor } from "../services/navigation";
import { useRole } from "../context/RoleContext";

// [UPDATED] PM-19: one sidebar with every menu the user is allowed to see, grouped by view
// (Admin, HR, Management, My team, My work). Clicking an item switches to that view itself, so the
// "View as" switch in the top bar is gone. Each block can be collapsed; the choice is remembered.
const COLLAPSE_KEY = "pms_sidebar_collapsed";

function readCollapsed() {
  try { return JSON.parse(localStorage.getItem(COLLAPSE_KEY) || "{}") || {}; } catch { return {}; }
}

export default function Sidebar({ open, onClose }) {
  const { activeView, setActiveView, availableViews, counters, profile, roleKeys } = useRole();
  const location = useLocation();
  const blocks = sidebarFor(availableViews, profile?.grants || null, roleKeys);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const single = blocks.length === 1;

  const toggle = (view) => {
    setCollapsed((current) => {
      const next = { ...current, [view]: !current[view] };
      try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify(next)); } catch { /* storage may be blocked */ }
      return next;
    });
  };

  const isActive = (view, path) => {
    if (view !== activeView) return false;
    if (path === "/dashboard" || path === "/team") return location.pathname === path;
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const open_ = (view) => {
    if (view !== activeView) setActiveView(view);
    onClose?.();
  };

  // [FIXED] Count shown next to a menu item:
  //  - pending work (Goal approvals, Progress approvals, Team assessments) comes from item.badgeKey,
  //    which AppLayout fills into counters (pendingApprovals / pendingProgress / pendingAssessments);
  //  - unread notifications for that page are added for the active view only, as before.
  const badgeFor = (view, path, badgeKey) => {
    const pending = badgeKey ? Number(counters?.[badgeKey]) || 0 : 0;
    const unread = view === activeView ? Number(counters?.unreadByLink?.[path]) || 0 : 0;
    // [NEW] "My goals": the sheet-status badge and the "Goals approved" notification describe the same event, so show one of them, not both added.
    if (badgeKey === "myGoalsUpdate") return Math.max(pending, unread);
    return pending + unread;
  };

  return (
    <>
      <div className={`sidebar-scrim ${open ? "is-open" : ""}`} onClick={onClose} aria-hidden="true" />
      <aside className={`sidebar ${open ? "is-open" : ""}`} aria-label="Main navigation">
        <div className="sidebar-brand">
          <img src={logo} alt="Teliolabs" className="sidebar-logo" />
          <button type="button" className="icon-btn sidebar-close" onClick={onClose} aria-label="Close menu">
            <FiX size={18} />
          </button>
        </div>
        <div className="sidebar-product">
          <span className="sidebar-product-name">Performance</span>
        </div>
        <nav className="sidebar-nav">
          {blocks.map(({ view, label, items }) => {
            const closed = !single && collapsed[view];
            return (
              <div key={view} className={`nav-section nav-view-block ${view === activeView ? "is-current" : ""}`}>
                {single ? (
                  <p className="nav-section-title">{label}</p>
                ) : (
                  <button type="button" className="nav-section-toggle" aria-expanded={!closed} onClick={() => toggle(view)}>
                    <span>{label}</span>
                    <FiChevronDown size={15} aria-hidden="true" className={closed ? "is-closed" : ""} />
                  </button>
                )}
                {!closed && items.map(({ label: itemLabel, path, icon: Icon, badgeKey }) => {
                  const count = badgeFor(view, path, badgeKey);
                  return (
                    <NavLink
                      key={`${view}:${path}`}
                      to={path}
                      onClick={() => open_(view)}
                      className={() => `nav-link ${isActive(view, path) ? "is-active" : ""}`}
                      aria-current={isActive(view, path) ? "page" : undefined}
                    >
                      <Icon size={17} aria-hidden="true" />
                      <span>{itemLabel}</span>
                      {count > 0 && <span className="nav-badge">{count}</span>}
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </nav>
        <div className="sidebar-foot">
          {/* Help & support sits in the fixed bottom area, so it is visible in every view and never scrolls away. */}
          <NavLink to="/help" end className={({ isActive: on }) => `nav-link ${on ? "is-active" : ""}`}>
            <FiHelpCircle size={17} aria-hidden="true" />
            <span>Help &amp; support</span>
          </NavLink>
          <NavLink to="/notifications" className={({ isActive: on }) => `nav-link ${on ? "is-active" : ""}`}>
            <span className="nav-dot" aria-hidden="true" />
            <span>Notifications</span>
            {counters?.unread > 0 && <span className="nav-badge">{counters.unread}</span>}
          </NavLink>
          <p className="sidebar-version">Teliolabs HRMS, PMS v1.0</p>
        </div>
      </aside>
    </>
  );
}